"use client"

import { useEffect } from "react"
import { toast } from "sonner"

import { useAuth } from "@/hooks/auth/use-auth"
import { useAutoReplyDetailsQuery } from "@/hooks/queries/email-auto-reply"
import { usePermissions } from "@/hooks/use-permissions"
import { CRM_PERMISSIONS } from "@/lib/auth/permissions.constants"

import { AutoReplyViewerDialog } from "./auto-reply-viewer-dialog"

interface AutoReplyReviewDialogProps {
  /** The reply to open; null keeps the dialog closed. */
  autoReplyId: string | null
  /** The agent whose reply it is — stamped on the feedback so the QA page's agent filter finds it. */
  agentId: string | null
  onClose: () => void
}

/**
 * The agent's reply opened from a thread: fetched on demand, reviewed in the
 * same dialog the QA page uses. A load that fails closes rather than spinning.
 */
export function AutoReplyReviewDialog({ autoReplyId, agentId, onClose }: AutoReplyReviewDialogProps) {
  const { user } = useAuth()
  const { can } = usePermissions()
  const details = useAutoReplyDetailsQuery(autoReplyId)

  useEffect(() => {
    if (!details.error) return
    toast.error(details.error.message)
    onClose()
  }, [details.error, onClose])

  return (
    <AutoReplyViewerDialog
      open={!!autoReplyId}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      autoReply={details.data ?? null}
      agentId={agentId ?? undefined}
      userTimezone={user?.timezone}
      showCrmLink={can(CRM_PERMISSIONS.VIEW)}
    />
  )
}
